import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { WidgetStatePlaceholderEnum } from '@shared/enums/widget-state-placeholder.enum';

import colorMap from '@shared/variables/chart-color.variable';
import { commonChartConfig, commonChartTooltip } from '@shared/variables/echart-common.variable';

import { CustomEventService } from '@shared/services/custom-event.service';
import { WidgetService } from '@shared/services/widget.service';

import { EChartOption } from 'echarts';

import { truncateRepositories } from '@shared/utils/object-mapper.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-closed-merge-request-widget',
  templateUrl: './closed-merge-request-widget.component.html'
})
export class ClosedMergeRequestWidgetComponent implements OnInit {

  REPOSITORY_LENGTH = 30;

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  widgetType: string;
  widgetFooter: Widget.FooterModel;
  headerAddon: string;
  headerComponents: [] = [];
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  @Input()
  set period(period: string) {
    if (period === 'FROM') {
      this.selectedDate = this.from.toString(10);
    } else if (period === 'TODAY') {
      this.selectedDate = `${period}`;
    } else if (period) {
      this.selectedDate = `FROM_LAST_${period}`;
    }
  };

  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  mergedData: any[];
  toolTipHTML: string;

  chartOption: EChartOption;
  echartsColors: string[];

  @ViewChild('toolTipTitle', { static: true }) toolTipTitle: TemplateRef<any>;
  tooltipContent: string;

  constructor(private widgetService: WidgetService,
              private customEventService: CustomEventService) { }

  ngOnInit(): void {
    this.toolTipHTML = this.headerComponents.join('<br>');
    this.loadWidgetData(true);

    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }
    });
  }

  loadWidgetData(initial?: boolean): void {
    this.loading = true;

    if (initial) {
      this.initialLoading = true;

      this.chartOption = {
        backgroundColor: commonChartConfig.bg,
        color: this.echartsColors
      };
    }

    this.widgetService.get(this.widgetId, this.selectedDate)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.loading = false;
        this.initialLoading = false;

        if (!response.data) {
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
          return;
        }

        this.showWidgetStatePlaceholder = undefined;
        this.mergedData = response.data.map((item, i, arr) => {
          item.calculatedClass = `count--1-of-${arr.length}`;
          return {
            ...item,
            displayRepo: item.repositories && item.repositories.length ? truncateRepositories(item.repositories) : [],
          };
        });

        this.echartsColors = [colorMap.get('short')[1]];

        const chartData = this.mergedData[1].chart;

        const formatter = (value): string => {
          const dataIndex = value[0].dataIndex;
          const data = chartData.xAxis[dataIndex];

          return `<div style="text-align: left"><a href="${data.link}" target="_blank">${data.title}</a><br>
                  ${data.displayData}<br>
                  ${data.openedBy}, ${data.mergedBy ? data.mergedBy + ',' : ''} ${data.mergedDate}<br>
                  ${data.repository}<br>
                  ${data.sourceBranch} --> ${data.targetBranch}</div>`;
        };

        this.chartOption = {
          color: this.echartsColors,
          tooltip: {
            ...commonChartTooltip,
            trigger: 'axis',
            formatter,
            enterable: true,
            position: ['-220px', '-90px']
          },
          grid: {
            top: 20,
            bottom: 0,
            left: 50,
            right: 50
          },
          legend: {
            show: false
          },
          xAxis: {
            type: 'category',

            data: chartData.data
          },
          yAxis: {
            show: false
          },
          series: [
            {
              type: 'bar',
              data: chartData.data,
              markLine: {
                data: [
                  {
                    type: 'average',
                    name: 'Average',
                  }
                ],
                label: {
                  formatter: 'Avg',
                }
              }
            }
          ]
        }
      }, error => {
        console.log('error while fetching widget data', error);
        this.loading = false;
        this.initialLoading = false;
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
      });
  }
}
